import { useEffect, useRef, useState } from 'preact/hooks';
import { Button, CloseButton } from './primitives.tsx';
import { copyTextWithSelectionFallback } from './clipboard.ts';

interface ShareDraftDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly cloudAvailable: boolean;
  readonly busy: boolean;
  readonly shareUrl: string | null;
  readonly shareCreatedAt: string | null;
  readonly stale: boolean;
  readonly message: string | null;
  readonly onClose: () => void;
  readonly onCreate: () => Promise<void>;
  readonly onRevoke: () => Promise<void>;
}

export function ShareDraftDialog(props: ShareDraftDialogProps) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const linkInputRef = useRef<HTMLInputElement | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'selected'>('idle');

  useEffect(() => {
    if (!props.open) return;
    setCopyState('idle');
    const previousFocus = document.activeElement as HTMLElement | null;
    dialogRef.current
      ?.querySelector<HTMLElement>('button:not([disabled]), input:not([disabled])')
      ?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [props.open]);

  useEffect(() => {
    if (!props.shareUrl) {
      setCopyState('idle');
      return;
    }
    linkInputRef.current?.focus();
    linkInputRef.current?.select();
  }, [props.shareUrl]);

  if (!props.open) return null;

  const copyLink = async () => {
    if (!props.shareUrl) return;
    const copied = await copyTextWithSelectionFallback(props.shareUrl, linkInputRef.current);
    setCopyState(copied ? 'copied' : 'selected');
  };

  const shareCreatedLabel = props.shareCreatedAt
    ? new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(new Date(props.shareCreatedAt))
    : null;

  return (
    <div class="modal-backdrop share-draft-backdrop" role="presentation">
      <section
        ref={dialogRef}
        class="share-draft-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-ride-title"
        data-testid="share-ride-dialog"
      >
        <header>
          <div>
            <h2 id="share-ride-title">Share Ride</h2>
            <span>{props.title}</span>
          </div>
          <CloseButton label="Close Share Ride" onClick={props.onClose} />
        </header>

        <div class="share-draft-body">
          <p class="share-draft-intro">Send this private Coaster Ride link to friends.</p>

          {!props.cloudAvailable && (
            <p class="share-draft-notice" role="status">
              Sign in through Buncha Games to create a private Coaster Ride link.
            </p>
          )}

          {props.shareUrl ? (
            <div class="share-draft-result">
              {props.stale && (
                <div class="share-draft-stale" role="alert">
                  <strong>This link shares your last saved version.</strong>
                </div>
              )}
              <label>
                <span>Coaster Ride Link</span>
                <input ref={linkInputRef} value={props.shareUrl} readOnly />
              </label>
              {shareCreatedLabel && (
                <p class="share-draft-created" role="status">
                  Link Created after most recent Test Ride at {shareCreatedLabel}.
                </p>
              )}
              <div class="share-draft-actions">
                <Button variant="primary" type="button" onClick={() => void copyLink()}>
                  {copyState === 'copied'
                    ? 'Copied!'
                    : copyState === 'selected'
                      ? 'Link Selected'
                      : 'Copy Link'}
                </Button>
                <Button type="button" onClick={props.onClose}>
                  Close
                </Button>
                <button
                  type="button"
                  class="share-draft-revoke"
                  disabled={props.busy}
                  onClick={() => void props.onRevoke()}
                >
                  {props.busy ? 'Revoking…' : 'Revoke Link'}
                </button>
              </div>
            </div>
          ) : (
            <div class="share-draft-preparing" role="status">
              <span>{props.busy ? 'Preparing your Coaster Ride link…' : 'Link unavailable.'}</span>
              {!props.busy && props.cloudAvailable && (
                <button type="button" onClick={() => void props.onCreate()}>
                  Try Again
                </button>
              )}
            </div>
          )}

          {props.message && (
            <p class="share-draft-notice" role="status">
              {props.message}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
